/*
 * <BeSlider> -- React wrapper for the .be-slider CSS component: the BeOS R5
 * slider. ONE control with two thumb styles (thumb_style is a property on
 * BSlider, not a separate class), verified against live R5 VM captures
 * (Notes/36):
 *
 *   - thumbStyle="block" (default): B_BLOCK_THUMB, the raised block with grip
 *     dots over a dark (80) groove.
 *   - thumbStyle="triangle": B_TRIANGLE_THUMB, the "Fonts > Cache" slider --
 *     an apex-up triangle on a lighter (184) bar.
 *
 * Both styles support UseFillColor (fillColor), hash-mark ticks
 * (ticks + hashMarks), SetBarColor (barColor), SetLimitLabels
 * (minLabel/maxLabel), and a rendered label/trailing header row (showLabel;
 * `label` alone stays aria-only for backwards compatibility).
 *
 * Controlled. Keyboard-operable (arrows) and accessible (role="slider"); drag
 * with the pointer. Value maps to --be-slider-value (0..100) for the CSS.
 * Pressing gives (non-authentic, requested) darkened-thumb feedback via CSS
 * :active -- no callback needed.
 *
 *   <BeSlider value={vol} onChange={setVol} label="Volume" />
 *   <BeSlider thumbStyle="triangle" value={cache} onChange={setCache}
 *             min={64} max={4096} fillColor="rgb(102, 152, 203)"
 *             label="Screen font cache size" showLabel trailing="1024 kB"
 *             minLabel="64 kB" maxLabel="4096 kB" />
 */

import { type HTMLAttributes, type KeyboardEvent, type PointerEvent, useImperativeHandle, useRef } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

/* Not BeProps<"div">: `onChange` is a numeric callback, not a DOM event, so the
 * DOM attributes come in with that one omitted. */
export type BeSliderProps = BeCommon &
	Omit<HTMLAttributes<HTMLDivElement>, "className" | "style" | "onChange"> & {
		value: number;
		min?: number;
		max?: number;
		step?: number;
		onChange?: (value: number) => void;
		/** Inert + dimmed, like every other control's `disabled`. Omitting onChange
		 * also makes it inert, but says nothing to CSS or to assistive tech. */
		disabled?: boolean;
		/** Thumb style. "block" = B_BLOCK_THUMB (default); "triangle" = B_TRIANGLE_THUMB. */
		thumbStyle?: "block" | "triangle";
		/** aria-label; also drawn above the bar when showLabel is set. */
		label?: string;
		/** Render the label (and trailing text) as a header row, like R5 DrawText. */
		showLabel?: boolean;
		/** Right-aligned text above the bar (R5 UpdateText, e.g. "1024 kB"). */
		trailing?: string;
		/** Number of hash-mark ticks (SetHashMarkCount). */
		ticks?: number;
		/** Where the ticks go (SetHashMarks). Default "bottom" when ticks is set. */
		hashMarks?: "bottom" | "top" | "both";
		/** Colour the filled portion left of the thumb (UseFillColor). */
		fillColor?: string;
		/** Override the bar interior colour (SetBarColor). Defaults: block 80, triangle 184. */
		barColor?: string;
		/** Left limit label below the bar (SetLimitLabels min). */
		minLabel?: string;
		/** Right limit label below the bar (SetLimitLabels max). */
		maxLabel?: string;
		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
		/**
		 * Participate in a `<BeBand>`: hand the label, bar and limit rows to the
		 * band's shared block tracks so the BAR lands on the band row.
		 *
		 * A slider can never be aligned by its box -- its bar moves with whichever
		 * optional rows are present (Notes/71 §3), so box alignment is correct in
		 * one of four configurations and silently 17px out in another. Only
		 * meaningful inside a `<BeBand>`.
		 */
		band?: boolean;
	};

export const BeSlider = beComponent<BeSliderProps, HTMLDivElement>("BeSlider", (props, be) => {
	const {
		value,
		min = 0,
		max = 100,
		step = 1,
		onChange: onChangeProp,
		disabled,
		thumbStyle = "block",
		label,
		showLabel,
		trailing,
		ticks,
		hashMarks,
		fillColor,
		barColor,
		minLabel,
		maxLabel,
		fluid,
		band,
		// consumed by be() off `props`; named here only to keep them out of the DOM
		scale: _scale,
		className: _className,
		style: _style,
		...rest
	} = props;

	// One gate for every handler: `disabled` and "no onChange given" are the same
	// inert state, so the existing !onChange guards below cover both.
	const onChange = disabled ? undefined : onChangeProp;

	// The component needs the element for pointer maths, and the caller may want
	// it too -- keep the internal ref and mirror it out through the forwarded one.
	const ref = useRef<HTMLDivElement>(null);
	const pct = ((value - min) / (max - min)) * 100;

	const clamp = (v: number) => Math.max(min, Math.min(max, v));
	const snap = (v: number) => clamp(Math.round(v / step) * step);

	const fromClientX = (clientX: number) => {
		const el = ref.current;
		if (!el) return value;
		const rect = el.getBoundingClientRect();
		const t = (clientX - rect.left) / rect.width;
		return snap(min + t * (max - min));
	};

	const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
		if (!onChange) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		onChange(fromClientX(e.clientX));
	};
	const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
		if (!onChange || e.buttons === 0) return;
		onChange(fromClientX(e.clientX));
	};
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) return;
		if (e.key === "ArrowRight" || e.key === "ArrowUp") {
			e.preventDefault();
			onChange(snap(value + step));
		} else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
			e.preventDefault();
			onChange(snap(value - step));
		}
	};

	const isTriangle = thumbStyle === "triangle";
	const showTicks = ticks != null && ticks > 1;
	const marks = hashMarks ?? "bottom";
	const showLimits = minLabel != null || maxLabel != null;
	const thumbClass = ["be-slider__thumb", isTriangle && "be-slider__thumb--triangle"].filter(Boolean).join(" ");

	const { ref: forwarded, ...root } = be(
		"be-slider",
		isTriangle && "is-triangle",
		showTicks && "has-ticks",
		disabled && "is-disabled",
		fluid && "is-fluid",
		band && "is-composite",
	);
	useImperativeHandle(forwarded, () => ref.current as never, []);

	const rootStyle: React.CSSProperties = {
		...root.style,
		"--be-slider-value": pct,
		...(showTicks ? { "--be-slider-ticks": ticks } : {}),
		...(fillColor ? { "--be-slider-fill": fillColor } : {}),
		/* The disabled twin can't be derived in CSS (R5 floors the tint to the
		 * CMAP8 ramp), so a caller-supplied bar gets the unfloored tint. The two
		 * built-in bars carry their exact constants in slider.css. */
		...(barColor ? { "--be-slider-bar": barColor, "--be-slider-bar-disabled": `color-mix(in srgb, ${barColor} 50%, #fff)` } : {}),
	};

	return (
		<div
			{...rest}
			ref={ref}
			className={root.className}
			style={rootStyle}
			role="slider"
			aria-valuenow={value}
			aria-valuemin={min}
			aria-valuemax={max}
			aria-label={label}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : 0}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onKeyDown={onKeyDown}
		>
			{showLabel && (label != null || trailing != null) && (
				<div className="be-progress-text">
					{pixelText(label, "be-progress-text__label")}
					<span className="be-progress-text__trailing">{pixelText(trailing)}</span>
				</div>
			)}
			<div className="be-slider__bar">
				<div className="be-slider__track">{fillColor && <div className="be-slider__fill" />}</div>
				{showTicks && (marks === "top" || marks === "both") && <div className="be-slider__ticks is-top" />}
				{showTicks && (marks === "bottom" || marks === "both") && <div className="be-slider__ticks" />}
				<div className={thumbClass} />
			</div>
			{showLimits && (
				<div className="be-slider__limits">
					{pixelText(minLabel)}
					{pixelText(maxLabel)}
				</div>
			)}
		</div>
	);
});
